/* Strategy builder — rule-graph authoring, backtesting and deployment.
 *
 * This panel replaces the read-only Quant screen with the full loop: write a
 * strategy as a rule graph, store it as a version, prove it on historical data,
 * and only then deploy it to the engine. The order of the sections on screen is
 * the order of that discipline.
 *
 * Principles the layout enforces rather than suggests:
 *
 *   * A backtest result is NEVER shown without its assumptions. The report's
 *     methodology, limitations and disclaimer render with every result — they
 *     are part of the number, not an appendix.
 *   * Everything below the backtest form is SIMULATED output and says so.
 *   * An instrument without an engine id can be backtested (history lives in
 *     the database) but not deployed (the engine does not serve it). The UI
 *     shows that distinction instead of letting a deploy fail late.
 *   * Saving a new version clears live approval. The API enforces it; the UI
 *     repeats the API's own note so the author is not surprised later.
 *
 * The rule grammar is the ENGINE's grammar. The editor emits exactly:
 *   { instruments: [engine ids], entry: {combinator, conditions}, exit: {...},
 *     risk: {position_size, max_position, stop_loss_pct, take_profit_pct,
 *            trailing_stop_pct} }
 * and each condition compares `left` against either another operand (`right`)
 * or a number (`value`) — one of the two, never both.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { useStore } from '@/store'
import { Panel } from '@/components/Panel'
import { Empty } from '@/components/viz'
import { fmtDateTime, fmtMoney, fmtPrice } from '@/lib/format'
import {
  POOL_FAMILIES, searchPool, type PoolCategory, type PoolFamily, type PoolTemplate,
} from '@/lib/strategyPool'
import {
  RULE_OPERANDS, RULE_OPS,
  type BacktestReport, type BacktestSummary, type BacktestTradeRow,
  type EquityPoint, type Instrument, type RuleGraph, type RuleOp,
  type RuleOperand, type Strategy,
} from '@/types'

// The engine's indicator bank runs FIXED periods. Shown as help text wherever
// an indicator operand can be picked, so nobody hunts for a period input that
// does not exist.
const INDICATOR_PERIODS =
  'Indicator periods are fixed in the engine: SMA 20, EMA 200, RSI 14, ' +
  'MACD 12/26/9, Bollinger 20/2, ATR 14, ADX 14, Stoch 14/3, Supertrend 10/3.'

// ===========================================================================
// Editable rule-graph state. Strings while editing (an empty number field is
// a real editing state); converted to the engine's numbers only on emit.
// ===========================================================================

interface CondDraft {
  left: RuleOperand
  op: RuleOp
  rightKind: 'operand' | 'number'
  right: RuleOperand
  value: string
}

interface RuleSetDraft {
  combinator: 'AND' | 'OR'
  conditions: CondDraft[]
}

interface RiskDraft {
  position_size: string
  max_position: string
  stop_loss_pct: string
  take_profit_pct: string
  trailing_stop_pct: string
}

const defaultCond = (left: RuleOperand, op: RuleOp, right: RuleOperand): CondDraft =>
  ({ left, op, rightKind: 'operand', right, value: '' })

const defaultEntry = (): RuleSetDraft =>
  ({ combinator: 'AND', conditions: [defaultCond('CLOSE', 'CROSSES_ABOVE', 'SMA')] })

const defaultExit = (): RuleSetDraft =>
  ({ combinator: 'OR', conditions: [defaultCond('CLOSE', 'CROSSES_BELOW', 'SMA')] })

const defaultRisk = (): RiskDraft => ({
  position_size: '1', max_position: '1',
  stop_loss_pct: '2', take_profit_pct: '4', trailing_stop_pct: '0',
})

/** Emit a rule set in exactly the engine's shape, or report what is wrong. */
function emitRuleSet(draft: RuleSetDraft, name: string):
  { ok: true; set: RuleGraph['entry'] } | { ok: false; error: string } {
  if (draft.conditions.length === 0) {
    return { ok: false, error: `the ${name} rule set has no conditions` }
  }
  const conditions = []
  for (const [i, c] of draft.conditions.entries()) {
    if (c.rightKind === 'number') {
      const n = Number(c.value)
      if (c.value.trim() === '' || !Number.isFinite(n)) {
        return { ok: false, error: `${name} condition ${i + 1} compares against a number, but none is entered` }
      }
      conditions.push({ left: c.left, op: c.op, value: n })
    } else {
      conditions.push({ left: c.left, op: c.op, right: c.right })
    }
  }
  return { ok: true, set: { combinator: draft.combinator, conditions } }
}

/** A stored rule set (engine shape) back into an editable draft, so a pool
 *  template lands in the editor exactly as if it had been typed. */
function draftFromRuleSet(set: RuleGraph['entry']): RuleSetDraft {
  return {
    combinator: set.combinator,
    conditions: set.conditions.map((c) => c.value !== undefined
      ? { left: c.left, op: c.op, rightKind: 'number', right: 'CONSTANT', value: String(c.value) }
      : { left: c.left, op: c.op, rightKind: 'operand', right: c.right ?? 'SMA', value: '' }),
  }
}

function draftFromRisk(risk: RuleGraph['risk']): RiskDraft {
  return {
    position_size: String(risk.position_size),
    max_position: String(risk.max_position),
    stop_loss_pct: String(risk.stop_loss_pct),
    take_profit_pct: String(risk.take_profit_pct),
    trailing_stop_pct: String(risk.trailing_stop_pct),
  }
}

// ===========================================================================
// Panel
// ===========================================================================

export function StrategyBuilder() {
  const can = useStore((s) => s.can)
  const canEdit = can('strategy:edit')
  const canDeploy = can('strategy:deploy')
  const canBacktest = can('backtest:run')

  const [strategies, setStrategies] = useState<Strategy[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const loadStrategies = useCallback(async () => {
    try {
      const s = await api.strategies()
      setStrategies(s.items ?? [])
      setFailed(null)
    } catch (e) {
      setFailed(e instanceof ApiError && e.status === 403
        ? 'Your role does not permit viewing strategies.'
        : 'The strategies endpoint could not be reached.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void loadStrategies() }, [loadStrategies])

  const selected = useMemo(
    () => strategies.find((s) => s.id === selectedId) ?? null,
    [strategies, selectedId])

  // Instrument selection lives at panel level: the rule graph, the backtest
  // and the deploy gate all read the same selection, so the three can never
  // silently disagree about which instruments a strategy is about.
  const [instruments, setInstruments] = useState<Instrument[]>([])

  // Reset the instrument selection when switching strategies — a graph
  // composed for one strategy must not leak into another.
  useEffect(() => { setInstruments([]) }, [selectedId])

  if (loading) return <Panel title="Strategy builder"><Empty>Loading…</Empty></Panel>
  if (failed) return <Panel title="Strategy builder"><Empty>{failed}</Empty></Panel>

  return (
    <Panel
      title="Strategy builder"
      actions={canEdit ? (
        <button onClick={() => setCreating((v) => !v)} style={{
          padding: '1px var(--sp-3)',
          background: creating ? 'var(--accent-bg)' : 'transparent',
          border: `1px solid ${creating ? 'var(--accent)' : 'var(--border)'}`,
          color: creating ? 'var(--accent)' : 'var(--text-muted)',
          borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-2xs)',
          cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          {creating ? 'Close' : '+ New strategy'}
        </button>
      ) : undefined}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <div style={helpBox}>
          Everything this screen produces below the backtest form is{' '}
          <b>SIMULATED</b> output. Backtest, paper and live results are never
          combined — a backtest is a claim about the past under assumptions, and
          those assumptions decide the number.
        </div>

        {creating && canEdit && (
          <CreateStrategyForm onCreated={async (s) => {
            setCreating(false)
            await loadStrategies()
            setSelectedId(s.id)
          }} />
        )}

        <StrategyList
          strategies={strategies}
          selectedId={selectedId}
          onSelect={(id) => setSelectedId(id === selectedId ? null : id)}
        />

        {!selected && strategies.length > 0 && (
          <Empty>Select a strategy to edit its rule graph, run a backtest or deploy it.</Empty>
        )}

        {selected && (
          <>
            <InstrumentSection instruments={instruments} onChange={setInstruments}
                               disabled={!canEdit && !canBacktest} />

            {canEdit ? (
              <GraphEditor
                strategy={selected}
                instruments={instruments}
                onVersionSaved={loadStrategies}
              />
            ) : (
              <Section title="Rule graph">
                <div style={helpBox}>
                  Your role does not permit editing strategies
                  (strategy:edit). The current version is
                  {' '}{selected.current_version != null ? `v${selected.current_version}` : 'not yet saved'}.
                </div>
              </Section>
            )}

            <BacktestSection strategy={selected} instruments={instruments}
                             canRun={canBacktest} />

            <DeploySection strategy={selected} instruments={instruments}
                           canDeploy={canDeploy} onChanged={loadStrategies} />
          </>
        )}
      </div>
    </Panel>
  )
}

// ===========================================================================
// Strategy list + creation
// ===========================================================================

function StrategyList({ strategies, selectedId, onSelect }: {
  strategies: Strategy[]
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  return (
    <Section title={`Strategies · ${strategies.length}`}>
      {strategies.length === 0 ? (
        <Empty>No strategies are registered. Create one to start building a rule graph.</Empty>
      ) : (
        <div className="scroll-x">
          <table className="qx">
            <thead>
              <tr>
                <th>Code</th><th>Name</th><th>Language</th><th>Status</th>
                <th className="r">Version</th><th>Live approved</th><th>Created</th>
              </tr>
            </thead>
            <tbody>
              {strategies.map((s) => (
                <tr key={s.id}
                    onClick={() => onSelect(s.id)}
                    style={{
                      cursor: 'pointer',
                      background: s.id === selectedId ? 'var(--accent-bg)' : undefined,
                    }}>
                  <td className="num">{s.code}</td>
                  <td style={{ fontWeight: 600 }}>{s.name}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{s.language}</td>
                  <td style={{ color: strategyStatusColor(s.status) }}>{s.status}</td>
                  <td className="num r">
                    {s.current_version != null ? `v${s.current_version}` : '—'}
                  </td>
                  <td style={{
                    color: s.approved_for_live ? 'var(--ok)' : 'var(--text-muted)',
                  }}>
                    {/* "not approved" is the safe default state, not a failure. */}
                    {s.approved_for_live ? 'approved' : 'not approved'}
                  </td>
                  <td className="num" style={{ color: 'var(--text-muted)' }}>
                    {new Date(s.created_at).toLocaleDateString('en-GB')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  )
}

function strategyStatusColor(status: string): string {
  switch (status?.toUpperCase()) {
    case 'RUNNING': case 'APPROVED': return 'var(--ok)'
    case 'STOPPED': case 'DRAFT': return 'var(--text-secondary)'
    case 'QUARANTINED': return 'var(--danger)'
    default: return 'var(--text-secondary)'
  }
}

function CreateStrategyForm({ onCreated }: { onCreated: (s: Strategy) => void }) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const codeOk = /^[A-Za-z0-9_-]{2,50}$/.test(code)

  async function submit() {
    setSubmitting(true)
    setError(null)
    try {
      const s = await api.createStrategy({
        code, name, description: description || null, language: 'RULE_GRAPH',
      })
      onCreated(s)
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Section title="New strategy">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', maxWidth: 520 }}>
        <Row2>
          <Field label="Code">
            <input value={code} onChange={(e) => setCode(e.target.value)}
                   placeholder="e.g. sma-cross-01" style={inputStyle} />
          </Field>
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Human-readable name" style={inputStyle} />
          </Field>
        </Row2>
        <Field label="Description (optional)">
          <input value={description} onChange={(e) => setDescription(e.target.value)}
                 placeholder="What the strategy is trying to capture" style={inputStyle} />
        </Field>
        {code && !codeOk && (
          <div style={{ fontSize: 9, color: 'var(--warn)' }}>
            Codes are 2–50 characters: letters, digits, underscore, hyphen.
          </div>
        )}
        {error && <Banner tone="danger">{error}</Banner>}
        <button
          disabled={submitting || !codeOk || !name.trim()}
          onClick={submit}
          style={primaryBtn(submitting || !codeOk || !name.trim())}
        >
          {submitting ? 'Creating…' : 'Create RULE_GRAPH strategy'}
        </button>
      </div>
    </Section>
  )
}

// ===========================================================================
// Instrument selection
// ===========================================================================

function InstrumentSection({ instruments, onChange, disabled }: {
  instruments: Instrument[]
  onChange: (list: Instrument[]) => void
  disabled?: boolean
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Instrument[]>([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)

  // Debounced instrument search — the master is large and every keystroke
  // firing a request would flood the API for no benefit.
  useEffect(() => {
    if (!query.trim()) { setResults([]); setSearchError(null); return }
    let cancelled = false
    const t = setTimeout(async () => {
      setSearching(true)
      try {
        const r = await api.instruments({ search: query.trim(), limit: '15' })
        if (!cancelled) { setResults(r.items ?? []); setSearchError(null) }
      } catch (e) {
        if (!cancelled) {
          setSearchError(e instanceof ApiError && e.status === 403
            ? 'Your role does not permit instrument search.'
            : 'Instrument search failed.')
        }
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [query])

  const add = (i: Instrument) => {
    if (!instruments.some((x) => x.id === i.id)) onChange([...instruments, i])
    setQuery('')
    setResults([])
  }

  return (
    <Section title={`Instruments · ${instruments.length}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        {instruments.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
            {instruments.map((i) => (
              <span key={i.id} title={i.engine_instrument_id == null
                      ? `${i.symbol} has no engine id: it can be backtested but not deployed`
                      : `engine id ${i.engine_instrument_id}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)',
                      padding: '3px var(--sp-2)',
                      background: 'var(--bg-input)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-xs)',
                    }}>
                <b>{i.symbol}</b>
                <span style={{ color: 'var(--text-muted)', fontSize: 9 }}>{i.exchange}</span>
                <span className="num" style={{
                  fontSize: 9,
                  color: i.engine_instrument_id == null ? 'var(--warn)' : 'var(--text-muted)',
                }}>
                  {/* The distinction that decides deployability, shown where the
                      instrument is chosen rather than where the deploy fails. */}
                  {i.engine_instrument_id == null
                    ? 'backtest only' : `engine #${i.engine_instrument_id}`}
                </span>
                {!disabled && (
                  <button
                    onClick={() => onChange(instruments.filter((x) => x.id !== i.id))}
                    aria-label={`Remove ${i.symbol}`}
                    style={{
                      background: 'none', border: 'none', color: 'var(--loss)',
                      cursor: 'pointer', fontSize: 'var(--fs-xs)', padding: 0,
                      fontFamily: 'inherit',
                    }}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {!disabled && (
          <div style={{ position: 'relative', maxWidth: 380 }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the instrument master (symbol or name)…"
              style={inputStyle}
            />
            {(results.length > 0 || searching || searchError) && query.trim() && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                background: 'var(--bg-panel)', border: '1px solid var(--border-strong)',
                borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-md)',
                maxHeight: 240, overflowY: 'auto',
              }}>
                {searching && (
                  <div style={{ padding: 'var(--sp-2)', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
                    Searching…
                  </div>
                )}
                {searchError && (
                  <div style={{ padding: 'var(--sp-2)', fontSize: 'var(--fs-xs)', color: 'var(--warn)' }}>
                    {searchError}
                  </div>
                )}
                {results.map((r) => (
                  <button key={r.id} onClick={() => add(r)} style={{
                    display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-2)',
                    width: '100%', padding: 'var(--sp-2)',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: 'var(--text-primary)', fontSize: 'var(--fs-xs)',
                    textAlign: 'left', fontFamily: 'inherit',
                  }}>
                    <span><b>{r.symbol}</b> <span style={{ color: 'var(--text-muted)' }}>{r.exchange}</span></span>
                    <span style={{
                      color: r.engine_instrument_id == null ? 'var(--warn)' : 'var(--text-muted)',
                      fontSize: 9,
                    }}>
                      {r.engine_instrument_id == null ? 'backtest only' : `engine #${r.engine_instrument_id}`}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ fontSize: 9, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Instruments with an <b>engine id</b> can be deployed; those marked
          "backtest only" are not served by the engine and can only run against
          stored history. The rule graph stores engine ids; the backtest uses
          the instruments' database ids.
        </div>
      </div>
    </Section>
  )
}

// ===========================================================================
// Rule-graph editor
// ===========================================================================

function GraphEditor({ strategy, instruments, onVersionSaved }: {
  strategy: Strategy
  instruments: Instrument[]
  onVersionSaved: () => Promise<void> | void
}) {
  const [entry, setEntry] = useState<RuleSetDraft>(defaultEntry)
  const [exit, setExit] = useState<RuleSetDraft>(defaultExit)
  const [risk, setRisk] = useState<RiskDraft>(defaultRisk)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState<string | null>(null)
  const [savedVersion, setSavedVersion] = useState<number | null>(null)
  const [loadedTemplate, setLoadedTemplate] = useState<PoolTemplate | null>(null)

  // A fresh editor per strategy. There is no endpoint to read a stored
  // version's graph back, so the editor always composes the NEXT version.
  useEffect(() => {
    setEntry(defaultEntry())
    setExit(defaultExit())
    setRisk(defaultRisk())
    setError(null)
    setSavedNote(null)
    setSavedVersion(null)
    setLoadedTemplate(null)
  }, [strategy.id])

  // A pool template replaces the whole draft — entry, exit and risk together.
  // Loading half a template would leave an exit that does not belong to the
  // entry, which is worse than either the template or the blank editor.
  const loadTemplate = (t: PoolTemplate) => {
    setEntry(draftFromRuleSet(t.entry))
    setExit(draftFromRuleSet(t.exit))
    setRisk(draftFromRisk(t.risk))
    setLoadedTemplate(t)
    setError(null)
    setSavedNote(null)
  }

  const build = useCallback((): { ok: true; graph: RuleGraph } | { ok: false; error: string } => {
    const e = emitRuleSet(entry, 'entry')
    if (!e.ok) return e
    const x = emitRuleSet(exit, 'exit')
    if (!x.ok) return x

    const posSize = Number.parseInt(risk.position_size, 10)
    const maxPos = Number.parseInt(risk.max_position, 10)
    if (!Number.isInteger(posSize) || posSize <= 0) {
      return { ok: false, error: 'position size must be a positive whole number' }
    }
    if (!Number.isInteger(maxPos) || maxPos <= 0) {
      return { ok: false, error: 'max position must be a positive whole number' }
    }
    const pct = (v: string, name: string): number | { err: string } => {
      const n = Number(v)
      return v.trim() === '' || !Number.isFinite(n) || n < 0 ? { err: `${name} must be a number ≥ 0` } : n
    }
    const sl = pct(risk.stop_loss_pct, 'stop loss %')
    if (typeof sl === 'object') return { ok: false, error: sl.err }
    const tp = pct(risk.take_profit_pct, 'take profit %')
    if (typeof tp === 'object') return { ok: false, error: tp.err }
    const ts = pct(risk.trailing_stop_pct, 'trailing stop %')
    if (typeof ts === 'object') return { ok: false, error: ts.err }

    return {
      ok: true,
      graph: {
        // Engine ids only: an instrument without one has no meaning to the
        // engine's evaluator. Backtests carry those instruments separately by
        // database id.
        instruments: instruments
          .map((i) => i.engine_instrument_id)
          .filter((v): v is number => v != null),
        entry: e.set,
        exit: x.set,
        risk: {
          position_size: posSize, max_position: maxPos,
          stop_loss_pct: sl, take_profit_pct: tp, trailing_stop_pct: ts,
        },
      },
    }
  }, [entry, exit, risk, instruments])

  async function save() {
    const built = build()
    if (!built.ok) { setError(built.error); return }
    setSaving(true)
    setError(null)
    setSavedNote(null)
    try {
      const r = await api.createStrategyVersion(strategy.id, built.graph)
      setSavedVersion(r.version)
      setSavedNote(r.note)
      await onVersionSaved()
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setSaving(false)
    }
  }

  const currentVersion = savedVersion ?? strategy.current_version

  return (
    <Section title={`Rule graph · ${strategy.code} · ${
      currentVersion != null ? `current v${currentVersion}` : 'no version saved yet'}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <div style={helpBox}>{INDICATOR_PERIODS}</div>

        <StrategyPoolPicker onLoad={loadTemplate} loadedId={loadedTemplate?.id ?? null} />

        {loadedTemplate && (
          <Banner tone="ok">
            Loaded <b>{loadedTemplate.name}</b> into the editor — edit any rule
            below, then save it as a version. {loadedTemplate.unique
              ? 'This rule-graph form is the simple rendition; the full strategy is in the Python lane template.'
              : `Modelled on ${loadedTemplate.source}.`}
          </Banner>
        )}

        <RuleSetEditor title="Entry rules" tone="var(--buy)" draft={entry} onChange={setEntry} />
        <RuleSetEditor title="Exit rules" tone="var(--sell)" draft={exit} onChange={setExit} />

        <div>
          <SubHeading>Risk</SubHeading>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: 'var(--sp-2)', maxWidth: 760,
          }}>
            <Field label="Position size">
              <input className="num" inputMode="numeric" value={risk.position_size}
                     onChange={(e) => setRisk({ ...risk, position_size: e.target.value })}
                     style={inputStyle} />
            </Field>
            <Field label="Max position">
              <input className="num" inputMode="numeric" value={risk.max_position}
                     onChange={(e) => setRisk({ ...risk, max_position: e.target.value })}
                     style={inputStyle} />
            </Field>
            <Field label="Stop loss %">
              <input className="num" inputMode="decimal" value={risk.stop_loss_pct}
                     onChange={(e) => setRisk({ ...risk, stop_loss_pct: e.target.value })}
                     style={inputStyle} />
            </Field>
            <Field label="Take profit %">
              <input className="num" inputMode="decimal" value={risk.take_profit_pct}
                     onChange={(e) => setRisk({ ...risk, take_profit_pct: e.target.value })}
                     style={inputStyle} />
            </Field>
            <Field label="Trailing stop %">
              <input className="num" inputMode="decimal" value={risk.trailing_stop_pct}
                     onChange={(e) => setRisk({ ...risk, trailing_stop_pct: e.target.value })}
                     style={inputStyle} />
            </Field>
          </div>
        </div>

        {error && <Banner tone="danger">{error}</Banner>}
        {savedNote && (
          <Banner tone="ok">
            Saved as v{savedVersion}. {savedNote}
          </Banner>
        )}

        <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
          <button disabled={saving} onClick={save} style={primaryBtn(saving)}>
            {saving ? 'Saving…' : `Save as version ${
              currentVersion != null ? currentVersion + 1 : 1}`}
          </button>
          <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>
            Saving a new version clears any previous live approval.
          </span>
        </div>
      </div>
    </Section>
  )
}

// ===========================================================================
// Strategy pool picker — ready templates, modelled on the public AlgoBulls
// strategy pool, rendered in the engine's own grammar. See lib/strategyPool.ts
// for the provenance of each and for the two engine facts (long-only, fixed
// indicator periods) that shape them.
// ===========================================================================

function StrategyPoolPicker({ onLoad, loadedId }: {
  onLoad: (t: PoolTemplate) => void
  loadedId: string | null
}) {
  const [open, setOpen] = useState(true)
  const [query, setQuery] = useState('')
  const [family, setFamily] = useState<PoolFamily | ''>('')
  const [category, setCategory] = useState<PoolCategory | ''>('')

  const rows = useMemo(() => searchPool(query, family, category), [query, family, category])

  return (
    <section className="qx-card">
      <header className="qx-card-head">
        <h2 className="qx-card-title">Strategy pool</h2>
        <div className="qx-card-actions">
          <button className="qx-btn qx-btn-ghost qx-btn-sm" onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : `Show ${rows.length}`}
          </button>
        </div>
      </header>

      {open && (
        <div className="qx-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Ready-made rule graphs to start from: Qorvex's own <b>ARM</b> first,
            then renditions of the regular strategies in the public AlgoBulls pool
            (EMA, MACD, RSI, Bollinger, VWAP, stochastic, Aroon, ATR trend). Loading
            one fills the editor; nothing is saved until you save a version. Every
            template is a starting point that must be backtested and paper traded —
            none is a performance claim.
          </div>

          <div className="qx-toolbar">
            <div className="qx-search" style={{ minWidth: 220, flex: 1 }}>
              <input className="qx-input" placeholder="Search the pool"
                     value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
            <div className="qx-chips">
              <button className={`qx-chip${category === '' ? ' on' : ''}`} onClick={() => setCategory('')}>All</button>
              <button className={`qx-chip${category === 'Intraday' ? ' on' : ''}`} onClick={() => setCategory('Intraday')}>Intraday</button>
              <button className={`qx-chip${category === 'Delivery' ? ' on' : ''}`} onClick={() => setCategory('Delivery')}>Delivery</button>
            </div>
            <div className="qx-chips">
              <button className={`qx-chip${family === '' ? ' on' : ''}`} onClick={() => setFamily('')}>Any family</button>
              {POOL_FAMILIES.map((f) => (
                <button key={f} className={`qx-chip${family === f ? ' on' : ''}`}
                        onClick={() => setFamily(family === f ? '' : f)}>{f}</button>
              ))}
            </div>
          </div>

          <div className="scroll-x" style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
            <table className="qx">
              <thead>
                <tr>
                  <th>Strategy</th>
                  <th>Mode</th>
                  <th>Family</th>
                  <th>Indicators</th>
                  <th>Defaults</th>
                  <th className="r">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} style={{ background: loadedId === t.id ? 'var(--accent-soft)' : undefined }}>
                    <td style={{ whiteSpace: 'normal', minWidth: 220 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span className="qx-link" style={{ cursor: 'default' }}>{t.name}</span>
                        {t.unique && <span className="qx-pill-dark">QORVEX</span>}
                      </div>
                      <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', marginTop: 2 }}>
                        {t.source}
                      </div>
                    </td>
                    <td><span className={`qx-mode ${t.category === 'Intraday' ? 'PAPER' : 'BACKTEST'}`} title={t.category}>{t.category === 'Intraday' ? 'ID' : 'DL'}</span></td>
                    <td>{t.family}</td>
                    <td style={{ whiteSpace: 'normal' }}>{t.indicators.join(', ')}</td>
                    <td style={{ whiteSpace: 'normal', minWidth: 260, fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }} title={t.description}>
                      {t.defaults}
                    </td>
                    <td className="r">
                      <button className="qx-btn qx-btn-primary qx-btn-sm" onClick={() => onLoad(t)}>
                        {loadedId === t.id ? 'Reload' : 'Load template'}
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="qx-empty">No template matches.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  )
}

function RuleSetEditor({ title, tone, draft, onChange }: {
  title: string
  tone: string
  draft: RuleSetDraft
  onChange: (d: RuleSetDraft) => void
}) {
  const setCond = (i: number, c: CondDraft) =>
    onChange({ ...draft, conditions: draft.conditions.map((x, k) => (k === i ? c : x)) })

  return (
    <div style={{
      padding: 'var(--sp-2)', background: 'var(--bg-input)',
      border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 'var(--sp-3)',
        marginBottom: 'var(--sp-2)',
      }}>
        <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: tone }}>
          {title}
        </span>
        {/* Combinator toggle: how the conditions below are joined. */}
        <div style={{ display: 'flex', gap: 2 }}>
          {(['AND', 'OR'] as const).map((c) => (
            <button key={c}
                    onClick={() => onChange({ ...draft, combinator: c })}
                    aria-pressed={draft.combinator === c}
                    style={{
                      padding: '1px var(--sp-2)',
                      background: draft.combinator === c ? 'var(--accent-bg)' : 'transparent',
                      border: `1px solid ${draft.combinator === c ? 'var(--accent)' : 'var(--border)'}`,
                      color: draft.combinator === c ? 'var(--accent)' : 'var(--text-muted)',
                      borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-2xs)',
                      fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                    }}>
              {c}
            </button>
          ))}
        </div>
        <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>
          {draft.combinator === 'AND'
            ? 'all conditions must hold' : 'any one condition suffices'}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
        {draft.conditions.map((c, i) => (
          <ConditionRow
            key={i}
            cond={c}
            onChange={(next) => setCond(i, next)}
            onRemove={draft.conditions.length > 1
              ? () => onChange({
                  ...draft,
                  conditions: draft.conditions.filter((_, k) => k !== i),
                })
              : undefined}
          />
        ))}
      </div>

      <button
        onClick={() => onChange({
          ...draft,
          conditions: [...draft.conditions, defaultCond('RSI', '<', 'CONSTANT')],
        })}
        style={{
          marginTop: 'var(--sp-2)', padding: '2px var(--sp-2)',
          background: 'transparent', border: '1px dashed var(--border-strong)',
          borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)',
          fontSize: 'var(--fs-2xs)', cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        + condition
      </button>
    </div>
  )
}

function ConditionRow({ cond, onChange, onRemove }: {
  cond: CondDraft
  onChange: (c: CondDraft) => void
  onRemove?: () => void
}) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(110px, 1fr) 120px 92px minmax(110px, 1fr) 18px',
      gap: 'var(--sp-2)', alignItems: 'center',
    }}>
      <select value={cond.left}
              aria-label="Left operand"
              onChange={(e) => onChange({ ...cond, left: e.target.value as RuleOperand })}
              style={{ ...inputStyle, padding: '3px var(--sp-2)' }}>
        {RULE_OPERANDS.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>

      <select value={cond.op}
              aria-label="Operator"
              onChange={(e) => onChange({ ...cond, op: e.target.value as RuleOp })}
              style={{ ...inputStyle, padding: '3px var(--sp-2)' }}>
        {RULE_OPS.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>

      {/* The grammar allows exactly one of `right` (operand) or `value`
          (number). The toggle makes the choice explicit instead of guessing
          from what was typed. */}
      <select value={cond.rightKind}
              aria-label="Compare against"
              onChange={(e) =>
                onChange({ ...cond, rightKind: e.target.value as 'operand' | 'number' })}
              style={{ ...inputStyle, padding: '3px var(--sp-2)' }}>
        <option value="operand">operand</option>
        <option value="number">number</option>
      </select>

      {cond.rightKind === 'operand' ? (
        <select value={cond.right}
                aria-label="Right operand"
                onChange={(e) => onChange({ ...cond, right: e.target.value as RuleOperand })}
                style={{ ...inputStyle, padding: '3px var(--sp-2)' }}>
          {RULE_OPERANDS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input className="num" inputMode="decimal" value={cond.value}
               aria-label="Comparison value"
               onChange={(e) => onChange({ ...cond, value: e.target.value })}
               placeholder="e.g. 30"
               style={{ ...inputStyle, padding: '3px var(--sp-2)' }} />
      )}

      {onRemove ? (
        <button onClick={onRemove} aria-label="Remove condition" style={{
          background: 'none', border: 'none', color: 'var(--loss)',
          cursor: 'pointer', fontSize: 'var(--fs-sm)', padding: 0,
          fontFamily: 'inherit',
        }}>
          ×
        </button>
      ) : <span />}
    </div>
  )
}

// ===========================================================================
// Backtests: run, poll, results — always with their assumptions
// ===========================================================================

type PollState =
  | { phase: 'idle' }
  | { phase: 'queued'; id: string; startedAt: number }
  | { phase: 'timeout'; id: string }

function BacktestSection({ strategy, instruments, canRun }: {
  strategy: Strategy
  instruments: Instrument[]
  canRun: boolean
}) {
  const today = new Date().toISOString().slice(0, 10)
  const yearAgo = new Date(Date.now() - 365 * 86400_000).toISOString().slice(0, 10)

  const [name, setName] = useState('')
  const [startDate, setStartDate] = useState(yearAgo)
  const [endDate, setEndDate] = useState(today)
  const [capital, setCapital] = useState('1000000')
  const [resolution, setResolution] = useState<'DAY' | 'HOUR' | 'MINUTE'>('DAY')

  const [poll, setPoll] = useState<PollState>({ phase: 'idle' })
  const [report, setReport] = useState<BacktestReport | null>(null)
  const [trades, setTrades] = useState<BacktestTradeRow[]>([])
  const [history, setHistory] = useState<BacktestSummary[]>([])
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const pollTimer = useRef<number | null>(null)

  const loadHistory = useCallback(async () => {
    try {
      setHistory(await api.backtests({ strategy_id: strategy.id }))
    } catch {
      /* backtest:view missing or endpoint down; the run form still works */
    }
  }, [strategy.id])

  useEffect(() => {
    setReport(null)
    setTrades([])
    setPoll({ phase: 'idle' })
    setError(null)
    void loadHistory()
  }, [loadHistory])

  // Poll the report until it reaches a terminal status. Every 3s, and give up
  // after 2 minutes rather than polling forever — the run stays listed under
  // past backtests either way.
  useEffect(() => {
    if (poll.phase !== 'queued') return
    let cancelled = false
    const tick = async () => {
      try {
        const r = await api.backtestReport(poll.id)
        if (cancelled) return
        if (r.status === 'COMPLETED' || r.status === 'FAILED') {
          setReport(r)
          setPoll({ phase: 'idle' })
          if (r.status === 'COMPLETED') {
            try { setTrades(await api.backtestTrades(poll.id)) } catch { setTrades([]) }
          } else {
            setTrades([])
          }
          void loadHistory()
          return
        }
      } catch {
        /* transient failure — keep polling until the deadline */
      }
      if (cancelled) return
      if (Date.now() - poll.startedAt > 120_000) {
        setPoll({ phase: 'timeout', id: poll.id })
        void loadHistory()
        return
      }
      pollTimer.current = window.setTimeout(tick, 3000)
    }
    pollTimer.current = window.setTimeout(tick, 3000)
    return () => {
      cancelled = true
      if (pollTimer.current != null) window.clearTimeout(pollTimer.current)
    }
  }, [poll, loadHistory])

  async function run() {
    setSubmitting(true)
    setError(null)
    setReport(null)
    setTrades([])
    try {
      const created = await api.createBacktest({
        name: name.trim() || `${strategy.code} ${startDate} → ${endDate}`,
        strategy_id: strategy.id,
        start_date: startDate,
        end_date: endDate,
        initial_capital: capital,
        data_resolution: resolution,
        // The backtester addresses instruments by DATABASE id — engine ids
        // are irrelevant here, which is why "backtest only" instruments work.
        instruments: instruments.map((i) => i.id),
      })
      setPoll({ phase: 'queued', id: created.backtest_id, startedAt: Date.now() })
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  async function openRun(id: string) {
    setError(null)
    try {
      const [r, t] = await Promise.allSettled([
        api.backtestReport(id), api.backtestTrades(id),
      ])
      if (r.status === 'fulfilled') setReport(r.value)
      else setError('The report could not be loaded.')
      setTrades(t.status === 'fulfilled' ? t.value : [])
    } catch {
      setError('The report could not be loaded.')
    }
  }

  const blocked = !canRun || instruments.length === 0 || !startDate || !endDate ||
                  !(Number(capital) > 0)

  return (
    <>
      <Section title="Backtest">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          {!canRun && (
            <Banner tone="warn">
              Your role does not permit running backtests (backtest:run).
            </Banner>
          )}

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 'var(--sp-2)', maxWidth: 900,
          }}>
            <Field label="Run name">
              <input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder={`${strategy.code} ${startDate} → ${endDate}`}
                     style={inputStyle} disabled={!canRun} />
            </Field>
            <Field label="Start date">
              <input type="date" value={startDate}
                     onChange={(e) => setStartDate(e.target.value)}
                     style={inputStyle} disabled={!canRun} />
            </Field>
            <Field label="End date">
              <input type="date" value={endDate}
                     onChange={(e) => setEndDate(e.target.value)}
                     style={inputStyle} disabled={!canRun} />
            </Field>
            <Field label="Initial capital (INR)">
              <input className="num" inputMode="decimal" value={capital}
                     onChange={(e) => setCapital(e.target.value)}
                     style={inputStyle} disabled={!canRun} />
            </Field>
            <Field label="Resolution">
              <select value={resolution}
                      onChange={(e) => setResolution(e.target.value as 'DAY' | 'HOUR' | 'MINUTE')}
                      style={inputStyle} disabled={!canRun}>
                {(['DAY', 'HOUR', 'MINUTE'] as const).map((r) =>
                  <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </div>

          <div style={{ fontSize: 9, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Stored history: daily bars from Aug 2023 to now for NIFTY50 and
            SENSEX30 names; 15-minute and hourly bars for roughly the last year.
            A window outside the stored data produces fewer trades, not an error.
          </div>

          {instruments.length === 0 && (
            <div style={{ fontSize: 9, color: 'var(--warn)' }}>
              Select at least one instrument above — a backtest with no
              instruments has nothing to trade.
            </div>
          )}

          {error && <Banner tone="danger">{error}</Banner>}

          {poll.phase === 'queued' && (
            <Banner tone="warn">
              Backtest queued — polling for the result every 3 seconds…
            </Banner>
          )}
          {poll.phase === 'timeout' && (
            <Banner tone="warn">
              Still running after 2 minutes. Polling has stopped; the run stays
              listed under past backtests below — open it there when it
              completes.
            </Banner>
          )}

          <button
            disabled={blocked || submitting || poll.phase === 'queued'}
            onClick={run}
            style={primaryBtn(blocked || submitting || poll.phase === 'queued')}
          >
            {submitting ? 'Queuing…'
              : poll.phase === 'queued' ? 'Running…'
              : 'Run backtest (simulated)'}
          </button>
        </div>
      </Section>

      {report && <BacktestResult report={report} trades={trades} />}

      <Section title={`Past backtests · ${history.length}`}>
        {history.length === 0 ? (
          <Empty>No backtests have been run for this strategy.</Empty>
        ) : (
          <div className="scroll-x">
            <table className="qx">
              <thead>
                <tr>
                  <th>Run</th><th>Status</th><th>Period</th>
                  <th className="r">Return</th><th className="r">Sharpe</th>
                  <th className="r">Max DD</th><th className="r">Win rate</th>
                  <th className="r">Trades</th><th>Completed</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}
                      onClick={() => void openRun(h.id)}
                      style={{
                        cursor: 'pointer',
                        background: report?.id === h.id ? 'var(--accent-bg)' : undefined,
                      }}>
                    <td style={{ fontWeight: 600 }}>{h.name}</td>
                    <td style={{ color: runStatusColor(h.status) }}>{h.status}</td>
                    <td style={{ color: 'var(--text-muted)' }}>
                      {h.start_date} → {h.end_date}
                    </td>
                    <MetricCell value={h.total_return} kind="pct" signed />
                    <MetricCell value={h.sharpe_ratio} kind="ratio" />
                    <MetricCell value={h.max_drawdown} kind="pct" tone="loss" />
                    <MetricCell value={h.win_rate} kind="pct" />
                    <td className="num r">{h.trades_count ?? '—'}</td>
                    <td className="num" style={{ color: 'var(--text-muted)' }}>
                      {h.completed_at ? fmtDateTime(h.completed_at) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </>
  )
}

function runStatusColor(status: string): string {
  switch (status?.toUpperCase()) {
    case 'COMPLETED': return 'var(--ok)'
    case 'RUNNING': case 'PENDING': case 'QUEUED': return 'var(--warn)'
    case 'FAILED': case 'CANCELLED': return 'var(--danger)'
    default: return 'var(--text-secondary)'
  }
}

/** Backtest result. Everything here is labelled SIMULATED, and the
 *  methodology, limitations and disclaimer render UNCONDITIONALLY — a result
 *  without its assumptions is not shown on this platform. */
function BacktestResult({ report, trades }: {
  report: BacktestReport
  trades: BacktestTradeRow[]
}) {
  const curve: EquityPoint[] = Array.isArray(report.equity_curve)
    ? report.equity_curve.filter((p) => p && Number.isFinite(p.t) && Number.isFinite(p.equity))
    : []

  return (
    <Section title={`Result · ${report.name} · SIMULATED`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <SimulatedBadge />
          <span style={{ fontSize: 'var(--fs-2xs)', color: runStatusColor(report.status) }}>
            {report.status}
          </span>
          <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
            {report.start_date} → {report.end_date} · {report.data_resolution}
            {' '}· capital {fmtMoney(report.initial_capital, report.currency || 'INR', true)}
          </span>
        </div>

        {report.status === 'FAILED' && (
          <Banner tone="danger">
            The run failed{report.error_message ? `: ${report.error_message}` : '.'}
          </Banner>
        )}

        {report.status === 'COMPLETED' && (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))',
              gap: 'var(--sp-2)',
            }}>
              <Hero label="Total return" value={fracPctText(report.total_return)}
                    tone={fracTone(report.total_return)} />
              <Hero label="CAGR" value={fracPctText(report.cagr)}
                    tone={fracTone(report.cagr)} />
              <Hero label="Sharpe" value={ratioText(report.sharpe_ratio)} />
              <Hero label="Sortino" value={ratioText(report.sortino_ratio)} />
              <Hero label="Max drawdown" value={fracPctText(report.max_drawdown, false)}
                    tone={report.max_drawdown != null ? 'loss' : undefined} />
              <Hero label="Win rate" value={fracPctText(report.win_rate, false)} />
              <Hero label="Profit factor" value={ratioText(report.profit_factor)} />
              <Hero label="Expectancy" value={moneyText(report.expectancy)} />
              <Hero label="Trades" value={report.trades_count != null
                ? String(report.trades_count) : '—'} />
              <Hero label="Total costs" value={moneyText(report.total_costs)} />
            </div>

            {/* Sample size travels with the ratios. */}
            <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>
              {report.trades_count != null
                ? `Computed over ${report.trades_count} trade(s). Ratios from a
                   small sample are not evidence of an edge.`
                : 'The trade count was not recorded, so the reliability of these ratios cannot be judged.'}
            </div>

            {curve.length > 1 && (
              <div>
                <SubHeading>Equity curve (mark-to-market, simulated)</SubHeading>
                <EquityCurve points={curve} currency={report.currency || 'INR'} />
              </div>
            )}

            {trades.length > 0 && (
              <div>
                <SubHeading>Trades · {trades.length}</SubHeading>
                <div className="scroll-x" style={{ maxHeight: 260, overflowY: 'auto' }}>
                  <table className="qx">
                    <thead>
                      <tr>
                        <th className="r">#</th><th>Symbol</th><th>Side</th>
                        <th className="r">Qty</th><th className="r">Entry</th>
                        <th className="r">Exit</th><th className="r">Costs</th>
                        <th className="r">Net P&L</th><th>Exit reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trades.map((t) => {
                        const pnl = t.net_pnl != null ? Number(t.net_pnl) : null
                        return (
                          <tr key={t.seq}>
                            <td className="num r" style={{ color: 'var(--text-muted)' }}>{t.seq}</td>
                            <td>{t.symbol}</td>
                            <td style={{ color: t.side === 'BUY' ? 'var(--buy)' : 'var(--sell)' }}>
                              {t.side}
                            </td>
                            <td className="num r">{fmtPrice(t.quantity, 0)}</td>
                            <td className="num r">{fmtPrice(t.entry_price)}</td>
                            <td className="num r">{fmtPrice(t.exit_price)}</td>
                            <td className="num r" style={{ color: 'var(--text-muted)' }}>
                              {fmtPrice(t.costs)}
                            </td>
                            <td className="num r" style={{
                              color: pnl == null ? 'var(--text-muted)'
                                   : pnl >= 0 ? 'var(--profit)' : 'var(--loss)',
                            }}>
                              {pnl == null ? '—' : `${pnl >= 0 ? '+' : ''}${fmtPrice(pnl)}`}
                            </td>
                            <td style={{ color: 'var(--text-muted)' }}>{t.exit_reason ?? '—'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* The assumptions ALWAYS render — completed, failed or otherwise.
            A backtest result is never shown without them. */}
        <div style={{
          padding: 'var(--sp-2) var(--sp-3)', background: 'var(--bg-input)',
          border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
          fontSize: 9, color: 'var(--text-secondary)', lineHeight: 1.6,
          display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)',
        }}>
          <div>
            <b style={{ color: 'var(--text-primary)' }}>Methodology.</b>{' '}
            {report.methodology_notes ?? 'The run did not record its methodology.'}
          </div>
          <div>
            <b style={{ color: 'var(--text-primary)' }}>Limitations.</b>{' '}
            {report.limitations_notes ?? 'The run did not record its limitations.'}
          </div>
          <div style={{ color: 'var(--text-muted)' }}>
            {report.disclaimer}
          </div>
        </div>
      </div>
    </Section>
  )
}

/** Equity over time, with drawdown from the running peak shaded beneath.
 *  X is real time from the report's own mark-to-market curve. */
function EquityCurve({ points, currency }: { points: EquityPoint[]; currency: string }) {
  const [hover, setHover] = useState<number | null>(null)
  const W = 640
  const H = 150
  const pad = 4

  const t0 = points[0]!.t
  const t1 = points[points.length - 1]!.t
  const tSpan = t1 - t0 || 1
  const values = points.map((p) => p.equity)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1

  const x = (t: number) => pad + ((t - t0) / tSpan) * (W - pad * 2)
  const y = (v: number) => H - pad - ((v - min) / span) * (H - pad * 2)

  let peak = -Infinity
  const peaks = values.map((v) => { peak = Math.max(peak, v); return peak })

  const line = points.map((p, i) =>
    `${i === 0 ? 'M' : 'L'}${x(p.t).toFixed(1)},${y(p.equity).toFixed(1)}`).join(' ')
  const under = [
    ...points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.t).toFixed(1)},${y(peaks[i]!).toFixed(1)}`),
    ...points.slice().reverse().map((p) => `L${x(p.t).toFixed(1)},${y(p.equity).toFixed(1)}`),
    'Z',
  ].join(' ')

  const active = hover != null ? points[hover] : null

  return (
    <div className="scroll-x">
      <svg width={W} height={H + 18} style={{ display: 'block', minWidth: 320 }}
           onMouseLeave={() => setHover(null)}
           onMouseMove={(e) => {
             const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect()
             const t = t0 + ((e.clientX - rect.left - pad) / (W - pad * 2)) * tSpan
             // nearest point in time
             let best = 0
             let bestD = Infinity
             points.forEach((p, i) => {
               const d = Math.abs(p.t - t)
               if (d < bestD) { bestD = d; best = i }
             })
             setHover(best)
           }}>
        <path d={under} fill="var(--loss)" opacity={0.16} />
        <path d={line} fill="none" stroke="var(--series-1)" strokeWidth={2}
              strokeLinejoin="round" />
        {active && (
          <>
            <line x1={x(active.t)} x2={x(active.t)} y1={pad} y2={H - pad}
                  stroke="var(--border-strong)" strokeWidth={1} />
            <circle cx={x(active.t)} cy={y(active.equity)} r={4}
                    fill="var(--series-1)" stroke="var(--bg-panel)" strokeWidth={2} />
          </>
        )}
      </svg>
      <div style={{
        fontSize: 9, color: 'var(--text-muted)', display: 'flex',
        justifyContent: 'space-between', maxWidth: W,
      }}>
        <span>
          {active
            ? `${new Date(active.t).toLocaleDateString('en-GB')} · equity ${
                fmtMoney(active.equity, currency, true)}`
            : `${new Date(t0).toLocaleDateString('en-GB')} → ${
                new Date(t1).toLocaleDateString('en-GB')} · ${points.length} points`}
        </span>
        <span>
          <span style={{ color: 'var(--loss)' }}>▨</span> drawdown from running peak
        </span>
      </div>
    </div>
  )
}

// ===========================================================================
// Deployment
// ===========================================================================

interface DeployAccount {
  id: string
  code: string
  name: string
  trading_mode: string
  is_active: boolean
}

function DeploySection({ strategy, instruments, canDeploy, onChanged }: {
  strategy: Strategy
  instruments: Instrument[]
  canDeploy: boolean
  onChanged: () => Promise<void> | void
}) {
  const [accounts, setAccounts] = useState<DeployAccount[]>([])
  const [accountId, setAccountId] = useState<string | null>(null)
  const [acctError, setAcctError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deployed, setDeployed] = useState<{
    engine_strategy_id?: number | null; engine_started?: boolean | null; note: string
  } | null>(null)
  const [stopReason, setStopReason] = useState('operator request')
  const [stopped, setStopped] = useState<string | null>(null)

  useEffect(() => {
    if (!canDeploy) return
    let cancelled = false
    void api.accounts()
      .then((a) => {
        if (cancelled) return
        const items = (a.items ?? []).filter((x) => x.is_active)
        setAccounts(items)
        setAccountId(items[0]?.id ?? null)
        if (items.length === 0) setAcctError('No active account is visible to you.')
      })
      .catch((e) => {
        if (cancelled) return
        setAcctError(e instanceof ApiError && e.status === 403
          ? 'Your role does not permit access to trading accounts.'
          : 'Accounts could not be loaded.')
      })
    return () => { cancelled = true }
  }, [canDeploy])

  useEffect(() => {
    setDeployed(null)
    setStopped(null)
    setError(null)
  }, [strategy.id])

  // The engine can only run instruments it serves. Selected instruments
  // without an engine id make the deploy button inert, with the reason on the
  // button itself — not a failure after the click.
  const unservable = instruments.filter((i) => i.engine_instrument_id == null)
  const noVersion = strategy.current_version == null && !deployed

  const blockReason =
    !canDeploy ? 'Your role does not permit deployment (strategy:deploy).'
    : noVersion ? 'Save a rule-graph version before deploying.'
    : unservable.length > 0
      ? `${unservable.map((i) => i.symbol).join(', ')} ${
          unservable.length === 1 ? 'is' : 'are'} not served by the engine — ` +
        'backtest only. Remove them to deploy.'
    : !accountId ? (acctError ?? 'No account is selected.')
    : null

  async function deploy() {
    if (!accountId) return
    setBusy(true)
    setError(null)
    setStopped(null)
    try {
      const r = await api.deployStrategy(strategy.id, {
        account_id: accountId, mode: 'PAPER',
      })
      setDeployed({
        engine_strategy_id: r.engine_strategy_id,
        engine_started: r.engine_started,
        note: r.note,
      })
      await onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function stop() {
    setBusy(true)
    setError(null)
    try {
      const r = await api.stopStrategy(strategy.id, stopReason.trim() || 'operator request')
      setDeployed(null)
      setStopped(`Stopped: ${r.reason}`)
      await onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setBusy(false)
    }
  }

  const running = strategy.status?.toUpperCase() === 'RUNNING' || deployed != null

  return (
    <Section title="Deploy">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', maxWidth: 560 }}>
        <div style={helpBox}>
          Deploy loads the strategy's <b>current version</b>
          {strategy.current_version != null ? ` (v${strategy.current_version})` : ''} into
          the running engine in <b>PAPER</b> mode — simulated execution on live
          data, no real orders. Live deployment requires separate approval and
          is not offered here.
        </div>

        {canDeploy && accounts.length > 0 && (
          <Field label="Account">
            <select value={accountId ?? ''} onChange={(e) => setAccountId(e.target.value)}
                    style={inputStyle}>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.code} · {a.trading_mode}</option>
              ))}
            </select>
          </Field>
        )}
        {canDeploy && acctError && <Banner tone="warn">{acctError}</Banner>}

        {error && <Banner tone="danger">{error}</Banner>}
        {stopped && <Banner tone="ok">{stopped}</Banner>}
        {deployed && (
          <Banner tone="ok">
            Deployed to the engine as strategy{' '}
            <b className="num">#{deployed.engine_strategy_id ?? '?'}</b>
            {' '}({deployed.engine_started ? 'started' : 'loaded, not started'}).{' '}
            {deployed.note}
          </Banner>
        )}

        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'stretch' }}>
          <button
            disabled={busy || blockReason != null}
            onClick={deploy}
            title={blockReason ?? 'Deploy the current version to the engine in PAPER mode'}
            style={{
              ...primaryBtn(busy || blockReason != null),
              flex: 1,
            }}
          >
            {busy ? 'Working…' : 'Deploy · PAPER (simulated)'}
          </button>
        </div>
        {blockReason && (
          <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{blockReason}</div>
        )}

        {canDeploy && running && (
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr auto', gap: 'var(--sp-2)',
            alignItems: 'end',
          }}>
            <Field label="Stop reason">
              <input value={stopReason} onChange={(e) => setStopReason(e.target.value)}
                     style={inputStyle} />
            </Field>
            <button disabled={busy} onClick={stop} style={{
              padding: 'var(--sp-2) var(--sp-3)', border: '1px solid var(--danger)',
              borderRadius: 'var(--radius-sm)', background: 'var(--danger-bg)',
              color: 'var(--danger)', cursor: 'pointer', fontWeight: 700,
              fontSize: 'var(--fs-xs)', fontFamily: 'inherit',
            }}>
              Stop strategy
            </button>
          </div>
        )}
      </div>
    </Section>
  )
}

// ===========================================================================
// Shared bits (house style: private per panel)
// ===========================================================================

function SimulatedBadge() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)',
      padding: '2px var(--sp-2)', borderRadius: 'var(--radius-sm)',
      border: '1px solid var(--mode-backtest)',
      background: 'color-mix(in srgb, var(--mode-backtest) 14%, transparent)',
      color: 'var(--mode-backtest)', fontSize: 'var(--fs-2xs)', fontWeight: 700,
      letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap',
    }}>
      <span aria-hidden style={{
        width: 6, height: 6, borderRadius: '50%', background: 'var(--mode-backtest)',
      }} />
      SIMULATED
    </span>
  )
}

/** Fraction string (0.12 = +12%) → percentage text. Null stays a dash: a
 *  metric that was not computed must not look like a zero. */
function fracPctText(v: string | null | undefined, signed = true): string {
  if (v == null || v === '') return '—'
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return `${signed && n >= 0 ? '+' : ''}${(n * 100).toFixed(2)}%`
}

function fracTone(v: string | null | undefined): 'profit' | 'loss' | undefined {
  if (v == null || v === '') return undefined
  const n = Number(v)
  if (!Number.isFinite(n)) return undefined
  return n >= 0 ? 'profit' : 'loss'
}

function ratioText(v: string | null | undefined): string {
  if (v == null || v === '') return '—'
  const n = Number(v)
  return Number.isFinite(n) ? n.toFixed(2) : '—'
}

function moneyText(v: string | null | undefined): string {
  if (v == null || v === '') return '—'
  const n = Number(v)
  return Number.isFinite(n) ? fmtMoney(n, 'INR', true) : '—'
}

function MetricCell({ value, kind, signed, tone }: {
  value: string | null | undefined
  kind: 'pct' | 'ratio'
  signed?: boolean
  tone?: 'loss'
}) {
  if (value == null || value === '' || !Number.isFinite(Number(value))) {
    return <td className="num r" style={{ color: 'var(--text-muted)' }}>—</td>
  }
  const n = Number(value)
  const color = tone === 'loss' ? 'var(--loss)'
              : signed ? (n >= 0 ? 'var(--profit)' : 'var(--loss)')
              : undefined
  return (
    <td className="num r" style={{ color }}>
      {kind === 'pct' ? fracPctText(value, !!signed) : n.toFixed(2)}
    </td>
  )
}

function Hero({ label, value, tone }: {
  label: string; value: string; tone?: 'profit' | 'loss'
}) {
  return (
    <div style={{
      padding: 'var(--sp-2) var(--sp-3)',
      background: 'var(--bg-input)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)',
    }}>
      <div style={{
        fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {label}
      </div>
      <div className="num" style={{
        fontSize: 'var(--fs-lg)', fontWeight: 700,
        color: tone === 'profit' ? 'var(--profit)'
             : tone === 'loss' ? 'var(--loss)' : 'var(--text-primary)',
      }}>
        {value}
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 style={{
        fontSize: 'var(--fs-2xs)', fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: 'var(--text-muted)',
        marginBottom: 'var(--sp-2)', paddingBottom: 3,
        borderBottom: '1px solid var(--border)',
      }}>
        {title}
      </h3>
      {children}
    </section>
  )
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 'var(--fs-2xs)', fontWeight: 700, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: 'var(--text-muted)',
      marginBottom: 'var(--sp-1)',
    }}>
      {children}
    </div>
  )
}

function Row2({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-2)' }}>
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', minWidth: 0 }}>
      <span style={{
        fontSize: 'var(--fs-2xs)', textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-muted)',
      }}>
        {label}
      </span>
      {children}
    </label>
  )
}

function Banner({ tone, children }: {
  tone: 'danger' | 'warn' | 'ok'; children: React.ReactNode
}) {
  const color = tone === 'danger' ? 'var(--danger)'
              : tone === 'warn' ? 'var(--warn)' : 'var(--ok)'
  const bg = tone === 'danger' ? 'var(--danger-bg)'
           : tone === 'warn' ? 'var(--warn-bg)' : 'var(--ok-bg)'
  return (
    <div style={{
      padding: 'var(--sp-2) var(--sp-3)', background: bg,
      border: `1px solid ${color}`, borderRadius: 'var(--radius-sm)',
      color, fontSize: 'var(--fs-xs)', lineHeight: 1.5,
    }}>
      {children}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: 'var(--sp-2)',
  background: 'var(--bg-input)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
  fontSize: 'var(--fs-sm)', fontFamily: 'inherit',
}

const helpBox: React.CSSProperties = {
  padding: 'var(--sp-2)', background: 'var(--bg-input)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
  fontSize: 9, color: 'var(--text-muted)', lineHeight: 1.5,
}

const primaryBtn = (disabled: boolean): React.CSSProperties => ({
  padding: 'var(--sp-2) var(--sp-4)', border: 'none',
  borderRadius: 'var(--radius-sm)', fontWeight: 700, fontSize: 'var(--fs-sm)',
  letterSpacing: '0.03em', fontFamily: 'inherit',
  background: disabled ? 'var(--bg-hover)' : 'var(--accent)',
  color: disabled ? 'var(--text-muted)' : '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer',
})
